import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Server, HardDrive, Cloud, Globe2, Wrench, ScrollText,
  LogOut, Plug, Database, Users, Boxes, ExternalLink, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.jsx';

/**
 * App shell for authenticated users — §13 Phase A.
 *
 * Sidebar (§4): --color-s-lblue rail, groups labelled in 11px eyebrows, active
 * item = --color-s-blue fill + --color-blue text at 600 + a 3px left bar. Not a
 * dark pill; GOV360 is a civic system and does not use dark chrome. Below
 * 1100px the rail collapses to icons (§8) — nobody drives this from a phone, so
 * there is no drawer.
 *
 * Topbar carries the 20px page title + a crumb, resolved from the route by
 * `resolvePage()`. That is where the mockup puts it, so pages do not render
 * their own <h1> (see PageHeader's note).
 *
 * NAV_GROUPS order is the operator's decision (§4): System first, with a
 * hairline divider under Dashboard so it still reads as "home" while its group
 * stays at the top, and Apps last — the only group whose items leave the panel.
 * This is presentation only — `canSee()`, the roles/feature/enabledKey filters,
 * the routes in App.jsx and every backend permission check are untouched, so no
 * bookmark breaks.
 */

// Each item: { to, label, icon, end?, roles?, feature?, external?, enabledKey?,
// dividerAfter? }. `end` marks an exact-match route (only Dashboard at "/");
// `roles` restricts by role; `feature` hides the item from a member without that
// §8c permission; `external` renders an <a> to a backend/redirect URL (the gated
// eachange / change hand-offs); `enabledKey` hides the item unless that flag on
// the user is true; `dividerAfter` draws the §4 hairline. Backend still enforces
// access — this is presentation only.
const NAV_GROUPS = [
  {
    label: 'System',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, dividerAfter: true },
      { to: '/integrations', label: 'Integrations', icon: Plug, feature: 'integrations' },
      { to: '/users', label: 'Users and access', icon: Users, roles: ['owner', 'admin'] },
      { to: '/audit', label: 'Audit log', icon: ScrollText, roles: ['owner', 'admin'] },
    ],
  },
  {
    label: 'Hosting',
    items: [
      { to: '/sites', label: 'Sites', icon: Server, feature: 'sites' },
      { to: '/servers', label: 'Servers', icon: HardDrive, feature: 'servers' },
    ],
  },
  {
    label: 'Server providers',
    items: [
      { to: '/cloud', label: 'Cloud servers', icon: Cloud, feature: 'cloud' },
    ],
  },
  {
    label: 'Storage and DNS',
    items: [
      { to: '/storage', label: 'Object storage', icon: Database, feature: 'storage' },
      { to: '/dns', label: 'DNS manager', icon: Globe2, feature: 'dns' },
    ],
  },
  {
    label: 'Utilities',
    items: [
      { to: '/tools', label: 'Tools', icon: Wrench, feature: 'tools' },
    ],
  },
  // Apps — separate applications, not panel pages. Last on purpose: every
  // group above it navigates inside the panel, this one leaves it, and
  // System (which it could be mistaken for) is at the very top.
  //
  // Azure manager and Oracle IP changer used to sit under "Server providers",
  // next to Cloud servers, as if they were provider integrations. They are not
  // — they are their own apps, so the group is where they belong regardless of
  // whether any of them is in use today. Oracle IP changer is here for that
  // structural reason (operator's call, 2026-07-31) even though Oracle is idle:
  // `changeEnabled` still keeps it hidden until the §9 gate is activated, so
  // this changes where it appears, not whether it appears.
  //
  // Sponsor audit joined them 2026-09-01 (operator's call), on the same terms:
  // `sponsorsEnabled` keeps it hidden until SPONSOR_AUDIT_GATE_SECRET is set on
  // the panel, so this can ship before the app is deployed anywhere without a
  // dead link appearing in anyone's sidebar.
  //
  // Every link here is the panel's own hand-off (`/api/*/launch`), not the bare
  // host — none of these apps has auth of its own, and eachange.webanceapp.com
  // 302s straight back to /api/eachange/launch, so linking the host only adds a
  // redirect hop. Nothing custom is built here either way: §8d, §9 and
  // apps/sponsor-audit/GATE-SA-panel-side.md are the auth, and these are plain
  // links to them.
  {
    label: 'Apps',
    items: [
      {
        external: '/api/eachange/launch',
        label: 'Azure manager',
        icon: Boxes,
        feature: 'eachange',
        enabledKey: 'eachangeEnabled',
      },
      {
        external: '/api/change/launch',
        label: 'Oracle IP changer',
        icon: RefreshCw,
        feature: 'change',
        enabledKey: 'changeEnabled',
      },
      {
        external: '/api/sponsors/launch',
        label: 'Sponsor audit',
        icon: ShieldCheck,
        feature: 'sponsors',
        enabledKey: 'sponsorsEnabled',
      },
      {
        external: '/api/deploypanel/launch',
        label: 'Deploy panel',
        icon: Boxes,
        feature: 'deploypanel',
        enabledKey: 'deploypanelEnabled',
      },
    ],
  },
];

// Topbar title + crumb per route. Longest prefix wins, so the order here only
// matters for readability. Sub-pages of /sites are resolved separately below
// because their title is the domain, which is a machine value (§2) and gets set
// in mono.
const PAGE_TITLES = [
  { to: '/servers', title: 'Servers', crumb: 'Hosting' },
  { to: '/cloud', title: 'Cloud servers', crumb: 'Server providers' },
  { to: '/storage', title: 'Object storage', crumb: 'Storage and DNS' },
  { to: '/dns', title: 'DNS manager', crumb: 'Storage and DNS' },
  { to: '/tools', title: 'Tools', crumb: 'Utilities' },
  { to: '/integrations', title: 'Integrations', crumb: 'System' },
  { to: '/users', title: 'Users and access', crumb: 'System' },
  { to: '/audit', title: 'Audit log', crumb: 'System' },
];

function resolvePage(pathname) {
  if (pathname === '/') {
    return { title: 'Dashboard', crumb: 'Everything on one box' };
  }
  const site = /^\/sites\/(.+)$/.exec(pathname);
  if (site) {
    return site[1] === 'new'
      ? { title: 'Create site', crumb: 'Hosting / Sites' }
      : { title: decodeURIComponent(site[1]), crumb: 'Hosting / Sites', mono: true };
  }
  if (pathname.startsWith('/sites')) {
    return { title: 'Sites', crumb: 'Hosting' };
  }
  const hit = PAGE_TITLES.find((p) => pathname.startsWith(p.to));
  return hit ?? { title: 'Webance', crumb: null };
}

function initials(user) {
  const source = user?.name || user?.email || '';
  const parts = source.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]).join('');
  return (letters || source.slice(0, 2) || '?').toUpperCase();
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const page = resolvePage(pathname);

  // Hide items a user can't use: `roles` restricts by role, `feature` by §8c
  // permission. owner/admin have `permissions.all` so they see every feature.
  // A null/unknown role/permissions (e.g. a pre-8a session) is treated
  // leniently so nothing a valid session can reach disappears — the backend
  // enforces access regardless.
  const perms = user?.permissions;
  const canSee = (item) => {
    if (item.enabledKey && user?.[item.enabledKey] !== true) return false;
    if (item.roles && user?.role && !item.roles.includes(user.role)) return false;
    if (item.feature && perms && !perms.all && perms.features?.[item.feature] !== true) return false;
    return true;
  };
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-screen text-t1">
      {/* Sidebar — icon rail below 1100px, full 248px above (§8). */}
      <aside className="flex flex-col shrink-0 w-16 wide:w-[248px] min-h-screen bg-s-lblue border-r border-bd">
        <div className="flex items-center gap-2.5 px-4 wide:px-5 py-[18px] border-b border-bd">
          <div className="grid place-items-center w-7 h-7 shrink-0 rounded-ctl bg-blue text-white text-[13px] font-bold tracking-[-0.02em]">
            W
          </div>
          <div className="hidden wide:block min-w-0">
            <b className="block text-[15px] font-semibold tracking-[-0.01em]">Webance</b>
            <span className="block text-[11px] text-t3">Control panel</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 wide:px-3 py-4">
          {groups.map((group) => (
            <div key={group.label} className="mb-[22px]">
              <p className="hidden wide:block px-2.5 mb-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
                {group.label}
              </p>
              {group.items.map((item, i) => (
                <div key={item.to ?? item.external}>
                  {item.external ? (
                    <ExternalNavItem href={item.external} icon={item.icon} label={item.label} />
                  ) : (
                    <NavItem to={item.to} end={item.end} icon={item.icon} label={item.label} />
                  )}
                  {item.dividerAfter && i < group.items.length - 1 && (
                    <div className="h-px my-2 mx-2.5 bg-bd" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 px-3 wide:px-4 py-3.5 border-t border-bd">
          <div
            className="grid place-items-center w-[30px] h-[30px] shrink-0 rounded-full bg-s-dgrey text-ic text-[11px] font-semibold"
            title={user?.email}
          >
            {initials(user)}
          </div>
          <div className="hidden wide:block flex-1 min-w-0">
            <b className="block text-[13px] font-medium truncate">
              {user?.name || user?.email}
            </b>
            <span className="block text-[11px] text-t3 truncate capitalize">
              {user?.role || user?.email}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
            className="hidden wide:grid place-items-center w-7 h-7 shrink-0 rounded-ctl text-ic hover:text-t1 hover:bg-s-3 cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center gap-3.5 h-16 shrink-0 px-5 wide:px-7 bg-white border-b border-bd">
          <h1
            className={[
              'text-xl font-semibold tracking-[-0.01em] truncate',
              page.mono ? 'font-mono text-[17px] tracking-normal' : '',
            ].join(' ')}
          >
            {page.title}
          </h1>
          {page.crumb && (
            <span className="hidden wide:block text-[13px] text-t3 truncate">
              {page.crumb}
            </span>
          )}
          <span className="flex-1" />
          <span className="px-2.5 py-[5px] rounded-ctl bg-s-green text-ok text-[11px] font-semibold uppercase tracking-[0.06em]">
            {import.meta.env.PROD ? 'Production' : 'Development'}
          </span>
          <button
            type="button"
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
            className="wide:hidden grid place-items-center w-8 h-8 shrink-0 rounded-ctl text-ic hover:text-t1 hover:bg-s-3 cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        <main className="flex-1 min-w-0 px-5 wide:px-7 pt-6 pb-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const ITEM_BASE = [
  'flex items-center gap-[11px] mb-px px-2.5 py-2 rounded-ctl',
  'border-l-[3px] text-sm select-none transition-colors',
  'wide:justify-start justify-center',
].join(' ');

// External link (e.g. the eachange SSO hand-off) — a full-page navigation to a
// backend URL, opened in a new tab. Styled like a nav item but never "active".
function ExternalNavItem({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={label}
      className={[ITEM_BASE, 'border-l-transparent text-t2 hover:bg-s-2'].join(' ')}
    >
      <Icon className="w-[17px] h-[17px] shrink-0 text-ic" strokeWidth={1.7} />
      <span className="hidden wide:block flex-1 truncate">{label}</span>
      <ExternalLink className="hidden wide:block w-3.5 h-3.5 shrink-0 text-ic-light" />
    </a>
  );
}

function NavItem({ to, end, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) =>
        [
          ITEM_BASE,
          isActive
            ? 'bg-s-blue border-l-blue text-blue font-semibold'
            : 'border-l-transparent text-t2 hover:bg-s-2',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={['w-[17px] h-[17px] shrink-0', isActive ? 'text-blue' : 'text-ic'].join(' ')}
            strokeWidth={1.7}
          />
          <span className="hidden wide:block truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}
